import * as Immutable from 'immutable';
import { createMigration } from './utils';

const LEGACY_FUNCTION_MARKER = '$fn';
const IDENTIFIER = '[A-Za-z_$][\\w$]*';
const NUMBER = '\\d+(?:\\.\\d+)?';
const STAGGER_BODY = `(?:\\1\\*(${NUMBER})|(${NUMBER})\\*\\1)`;
const STAGGER_SOURCES = [
    new RegExp(`^\\(?(${IDENTIFIER})\\)?=>${STAGGER_BODY}$`),
    new RegExp(`^function\\((${IDENTIFIER})\\)\\{return${STAGGER_BODY};?\\}$`),
];

type PlainObject = Record<string, unknown>;

function isFunctionMarker(value: unknown): value is PlainObject {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        LEGACY_FUNCTION_MARKER in value
    );
}

function staggerDelayPerIndex(marker: PlainObject): number | null {
    const source = marker[LEGACY_FUNCTION_MARKER];
    if (typeof source !== 'string') {
        return null;
    }
    const compact = source.replace(/\s+/g, '');
    for (const pattern of STAGGER_SOURCES) {
        const match = compact.match(pattern);
        if (match) {
            return Number(match[2] ?? match[3]);
        }
    }
    return null;
}

function migrateConfigNode(node: unknown): unknown {
    if (Array.isArray(node)) {
        return node.filter((item) => !isFunctionMarker(item)).map(migrateConfigNode);
    }
    if (typeof node !== 'object' || node === null) {
        return node;
    }
    const result: PlainObject = {};
    for (const [key, value] of Object.entries(node)) {
        if (!isFunctionMarker(value)) {
            result[key] = migrateConfigNode(value);
            continue;
        }
        const delayPerIndex = key === 'animationDelay' ? staggerDelayPerIndex(value) : null;
        if (delayPerIndex !== null) {
            result.animationDelayPerIndex = delayPerIndex;
        }
    }
    return result;
}

function migrateChart(map: Immutable.Map<string, unknown>, assetPath: Array<string | number>) {
    const config = map.getIn([...assetPath, 'config']) as Immutable.Map<string, unknown> | null;
    if (!config) {
        return;
    }
    const migrated = Immutable.fromJS(migrateConfigNode(config.toJS()));
    if (!Immutable.is(migrated, config)) {
        map.setIn([...assetPath, 'config'], migrated);
    }
}

function migrateAssets(map: Immutable.Map<string, unknown>, listPath: Array<string | number>) {
    const assets = map.getIn(listPath) as
        | Immutable.List<Immutable.Map<string, unknown>>
        | undefined;
    assets?.forEach((asset, index) => {
        const assetPath = [...listPath, index];
        if (asset.get('children')) {
            migrateAssets(map, [...assetPath, 'children']);
        }
        if (asset.get('classType') === 'ChartAsset') {
            migrateChart(map, assetPath);
        }
    });
}

export const migrationTo9_249 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            map.get('slides').forEach((_scene: unknown, i: number) => {
                map.setIn(
                    ['slides', i],
                    this.migrateScene(map.getIn(['slides', i]) as Immutable.Map<string, unknown>),
                );
            });
        });
    },
    migrateScene(scene) {
        return scene.withMutations((map) => {
            migrateAssets(map, ['children']);
            migrateAssets(map, ['stickers']);
            const subslides = map.get('subslides') as Immutable.List<unknown> | undefined;
            subslides?.forEach((_subslide, index) => {
                migrateAssets(map, ['subslides', index, 'children']);
            });
        });
    },
});
